import { createAsync } from "@solidjs/router"
import { createMemo, createSignal, For, Show } from "solid-js";

import { updateDB } from "~/_server";
import { getAdminApprovedBodies } from "~/lib/queries";

const capitalize = ( str: string ) =>
	str.charAt( 0 ).toUpperCase() + str.slice( 1 ).toLowerCase();

const toDateInputValue = ( seconds?: number | null ) =>
	seconds ? new Date( seconds * 1000 ).toISOString().slice( 0, 10 ) : '';

export const ApprovedBodies = () => {
	const [filter, setFilter] = createSignal( '' );
	const [showAll, setShowAll] = createSignal( false );
	const [overrides, setOverrides] = createSignal<Record<string, number>>( {} );

	const bodiesData = createAsync( () => getAdminApprovedBodies() );

	// Merge in locally-saved edits without refetching, so saving a date doesn't
	// tear down and rebuild every row in the list mid-edit (loses input focus).
	const bodies = createMemo( () => {
		const ov = overrides();
		return ( bodiesData() ?? [] ).map( ab => ov[ab.pk] != null ? { ...ab, piRenewalDate: ov[ab.pk] } : ab );
	} );

	const filteredBodies = createMemo( () => bodies().filter( ab => {
		const term = filter().trim().toLowerCase();

		if ( term !== '' ) {
			const name = ab.name ?? ab.address?.title ?? ab.address?.name ?? '';
			const address = [ab.address?.addr1, ab.address?.addr2, ab.address?.town, ab.address?.county, ab.address?.postcode].filter( Boolean ).join( ', ' );
			const contacts = [ab.coordinator?.name, ab.coordinator?.email, ab.deputy?.name, ab.deputy?.email].filter( Boolean ).join( ' ' );

			return [name, address, contacts].join( ' ' ).toLowerCase().includes( term );
		}

		return showAll() || ab.status === 'active';
	} ) );

	const setPiRenewalDate = async ( ab, value: string ) => {
		if ( !value ) return;

		const piRenewalDate = Math.floor( new Date( `${value}T00:00:00Z` ).getTime() / 1000 );

		await updateDB( ab.pk, ab.sk, { piRenewalDate } );

		setOverrides( o => ( { ...o, [ab.pk]: piRenewalDate } ) );
	};

	return ( <div class="px-4 sm:px-6 lg:px-8">
		<div class="sm:flex sm:items-center">
			<div class="sm:flex-auto">
				<h1 class="text-base font-semibold text-gray-900">Approved Bodies</h1>
			</div>
		</div>

		<div class="sm:flex sm:items-center text-sm mt-2">
			Filter: <input title="Search approved bodies" onInput={e => setFilter( e.target.value )} type="text" class="block rounded-md min-w-2xl ml-2 bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-primary sm:text-sm/6 w-10" />

			<label class="ml-4 flex items-center gap-2 whitespace-nowrap text-gray-700">
				<input type="checkbox" checked={showAll()} onChange={e => setShowAll( e.currentTarget.checked )} />
				Show all (including dormant)
			</label>
		</div>

		<div class="mt-8 flow-root">
			<div class="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
				<div class="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
					<table class="relative min-w-full divide-y divide-gray-300">
						<thead>
							<tr class="even:bg-gray-50">
								<th scope="col" class="py-3.5 pr-3 pl-4 text-left text-sm font-semibold text-gray-900 sm:pl-0">Name</th>
								<th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Address</th>
								<th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Status</th>
								<th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Users</th>
								<th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">PI Renewal Date</th>
							</tr>
						</thead>
						<tbody class="divide-y divide-gray-200">
							<For each={filteredBodies()}>
								{
									ab => (
										<tr class="even:bg-gray-50">
											<td class="py-4 pr-3 pl-4 text-sm font-medium text-gray-900 sm:pl-0">{ab.name ?? ab.address?.title ?? ab.address?.name ?? 'Unnamed'}</td>
											<td class="px-3 py-4 text-sm text-gray-500">
												<div>{[ab.address?.addr1, ab.address?.addr2, ab.address?.town, ab.address?.county, ab.address?.postcode].filter( Boolean ).join( ', ' ) || '-'}</div>
												<Show when={ab.coordinator?.name || ab.coordinator?.email}>
													<div class="text-xs text-gray-400 mt-1">Coordinator: {[ab.coordinator?.name, ab.coordinator?.email].filter( Boolean ).join( ' – ' )}</div>
												</Show>
												<Show when={ab.deputy?.name || ab.deputy?.email}>
													<div class="text-xs text-gray-400">Deputy: {[ab.deputy?.name, ab.deputy?.email].filter( Boolean ).join( ' – ' )}</div>
												</Show>
											</td>
											<td class="px-3 py-4 text-sm text-gray-500">{capitalize( ab.status )}</td>
											<td class="px-3 py-4 text-sm text-gray-500">{ab.userCount}</td>
											<td class="px-3 py-4 text-sm text-gray-500">
												<input
													type="date"
													class="rounded-md border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
													value={toDateInputValue( ab.piRenewalDate )}
													onChange={e => setPiRenewalDate( ab, e.currentTarget.value )}
												/>
											</td>
										</tr>
									)
								}
							</For>
						</tbody>
					</table>
				</div>
			</div>
		</div>
	</div> );
}
